import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n/i18n';
import { ITEM_STATUS_LABEL, downloadCsv, fetchSessions, isoDay, itemLabel, resultText, type Session } from './api';
import { addDays, daysBetween, parseDay, shortTime, sortSessions, today, useDates } from './dates';
import PlannerDialog from './PlannerDialog';
import { StatusWord, sessionName } from './SessionParts';
import DateField from '../components/DateField';

function termOf(day: string): { from: string; to: string } {
  const d = parseDay(day);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  if (m >= 9) return { from: `${y}-09-01`, to: `${y}-12-31` };
  if (m < 4 || (m === 4 && d.getDate() < 15)) return { from: `${y}-01-01`, to: `${y}-04-14` };
  return { from: `${y}-04-15`, to: `${y}-07-31` };
}

export default function ExportDialog({ learnerId, learnerName, onClose }: { learnerId: string; learnerName: string | null; onClose: () => void }) {
  const t = useT();
  const id = useId();
  const now = today();
  const [from, setFrom] = useState(addDays(now, -41));
  const [to, setTo] = useState(now);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState<Session[] | null>(null);

  useEffect(() => {
    if (!printing) return;
    const done = () => {
      document.body.classList.remove('planner-printing');
      setPrinting(null);
    };
    document.body.classList.add('planner-printing');
    window.addEventListener('afterprint', done, { once: true });
    window.print();
    return () => {
      window.removeEventListener('afterprint', done);
      document.body.classList.remove('planner-printing');
    };
  }, [printing]);

  function check(): boolean {
    if (!from || !to) { setError(t('Choose both dates.')); return false; }
    if (to < from) { setError(t('The end date must be on or after the start date.')); return false; }
    if (daysBetween(from, to).length > 400) { setError(t('Choose a range of up to 400 days.')); return false; }
    setError(null);
    return true;
  }

  async function csv() {
    if (!check()) return;
    setBusy(true);
    try {
      await downloadCsv(learnerId, from, to);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('The spreadsheet could not be made just now.'));
    } finally {
      setBusy(false);
    }
  }

  async function print() {
    if (!check()) return;
    setBusy(true);
    try {
      setPrinting(sortSessions(await fetchSessions(learnerId, from, to)));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('The sessions could not be loaded just now.'));
    } finally {
      setBusy(false);
    }
  }

  const pick = (r: { from: string; to: string }) => { setFrom(r.from); setTo(r.to); setError(null); };
  const year = parseDay(now).getFullYear();

  return (
    <>
      <PlannerDialog title={t('Export')} onClose={onClose} className="planner-export-dialog">
        <p className="modal-body">{t('Choose the dates to include. The export lists every session in those dates, what was planned, the results and the notes.')}</p>
        <div className="toolbar planner-quick">
          <button type="button" className="btn secondary" onClick={() => pick({ from: addDays(now, -41), to: now })}>{t('Last 6 weeks')}</button>
          <button type="button" className="btn secondary" onClick={() => pick(termOf(now))}>{t('This term')}</button>
          <button type="button" className="btn secondary" onClick={() => pick({ from: isoDay(new Date(year, 0, 1)), to: isoDay(new Date(year, 11, 31)) })}>{t('This year')}</button>
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor={`${id}-from`}>{t('From')}</label>
            <DateField id={`${id}-from`} value={from} onChange={(v) => setFrom(v ?? '')} />
          </div>
          <div className="field">
            <label htmlFor={`${id}-to`}>{t('To')}</label>
            <DateField id={`${id}-to`} value={to} min={from} onChange={(v) => setTo(v ?? '')} />
          </div>
        </div>
        {error && <div className="error" role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" disabled={busy} onClick={() => void csv()}>{t('Download spreadsheet (CSV)')}</button>
          <button type="button" className="btn secondary" disabled={busy} onClick={() => void print()}>{t('Print or save as PDF')}</button>
        </div>
      </PlannerDialog>
      {printing && createPortal(<PrintSummary sessions={printing} from={from} to={to} learnerName={learnerName} />, document.body)}
    </>
  );
}

function PrintSummary({ sessions, from, to, learnerName }: { sessions: Session[]; from: string; to: string; learnerName: string | null }) {
  const t = useT();
  const d = useDates();
  return (
    <div className="planner-print">
      <h1>{learnerName ? t('Plan for {name}', { name: learnerName }) : t('Plan')}</h1>
      <p>{t('From {from} to {to}', { from: d.long(from), to: d.long(to) })}</p>
      {sessions.length === 0 && <p>{t('No sessions in these dates.')}</p>}
      {sessions.map((s) => {
        const time = shortTime(s.startTime);
        return (
          <section key={s.id} className="planner-print-session">
            <h2>{d.long(s.day)}{time && <> · {time}</>} · {sessionName(s, t('Session'))}</h2>
            <p>
              <StatusWord status={s.status} />
              {s.who && <> · {t('Run by {who}', { who: s.who })}</>}
              {s.minutes != null && <> · {t('{n} minutes', { n: s.minutes })}</>}
            </p>
            {s.items.length > 0 && (
              <ol>
                {s.items.map((i) => {
                  const r = resultText(i);
                  return (
                    <li key={i.id}>
                      <strong>{itemLabel(i)}</strong> · {t(ITEM_STATUS_LABEL[i.status])}
                      {i.targetStatement && <div>{t('Target: {target}', { target: i.targetStatement })}</div>}
                      {r && <div>{t('Result: {result}', { result: r })}</div>}
                      {i.result?.prompt && <div>{t('Help given: {level}', { level: t(i.result.prompt) })}</div>}
                      {i.result?.text && <div>{i.result.text}</div>}
                    </li>
                  );
                })}
              </ol>
            )}
            {s.note && <p><strong>{t('Note:')}</strong> {s.note}</p>}
          </section>
        );
      })}
    </div>
  );
}
